import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API_URL = `${API_BASE}/customers`;

const INPUT_CLASS =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none";

const pill = (cls, text) => (
  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${cls}`}>{text}</span>
);

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingApps, setPendingApps] = useState(0);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  useEffect(() => {
    loadCustomers();
    loadPendingApplications();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to load customers:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // Best effort — just drives the badge on the Applications tab.
  const loadPendingApplications = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/applications?status=Pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setPendingApps(data.length);
    } catch {
      /* ignore */
    }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();

    return customers.filter((c) => {
      const id = c.customerRef || c.customerId || c._id?.slice(-6).toUpperCase() || "";
      const name = c.name || c.contactName || c.customerName || "";
      const company = c.companyName || c.company || c.businessName || "";
      const email = c.email || "";
      const type = c.customerType || c.type || "general";

      const matchesQuery =
        q === "" ||
        name.toLowerCase().includes(q) ||
        company.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q);

      const matchesType = typeFilter === "All" || type.toLowerCase() === typeFilter.toLowerCase();

      return matchesQuery && matchesType;
    });
  }, [customers, query, typeFilter]);

  return (
    <div className="w-full p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500">Find, manage, and edit customer accounts.</p>
        </div>
        <Link
          to="/dashboard/customers/new"
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={16} /> New Customer
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <span className="-mb-px border-b-2 border-slate-900 px-4 py-2 text-sm font-semibold text-slate-900">
          Customers
        </span>
        <Link
          to="/dashboard/applications"
          className="-mb-px flex items-center gap-2 border-b-2 border-transparent px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-900"
        >
          Applications
          {pendingApps > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">{pendingApps}</span>
          )}
        </Link>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search name, company, email, or customer ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${INPUT_CLASS} pl-9`}
            />
          </div>

          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={INPUT_CLASS}>
            <option>All</option>
            <option value="livestock">Livestock</option>
            <option value="freight">Freight</option>
            <option value="general">General</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-4 font-semibold">ID</th>
                <th className="py-2 pr-4 font-semibold">Customer</th>
                <th className="py-2 pr-4 font-semibold">Contact</th>
                <th className="py-2 pr-4 font-semibold">Email</th>
                <th className="py-2 pr-4 font-semibold">Phone</th>
                <th className="py-2 pr-4 font-semibold">Type</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Portal</th>
                <th className="py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="py-10 text-center text-slate-500">Loading customers...</td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((c) => {
                  const id = c.customerRef || c.customerId || c._id?.slice(-6).toUpperCase();
                  const customerName = c.companyName || c.company || c.businessName || "Unnamed Customer";
                  const contactName = c.contactName || c.name || "-";
                  const type = c.customerType || c.type || "general";
                  const status = c.status || "active";

                  return (
                    <tr key={c._id || id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 pr-4 font-mono text-xs text-slate-500">{id}</td>
                      <td className="py-3 pr-4">
                        <Link to={`/dashboard/customers/${c._id}`} className="font-semibold text-slate-900 hover:underline">
                          {customerName}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-slate-700">{contactName}</td>
                      <td className="py-3 pr-4 text-slate-600">{c.email || "-"}</td>
                      <td className="py-3 pr-4 text-slate-600">{c.phone || "-"}</td>
                      <td className="py-3 pr-4 capitalize text-slate-700">{type}</td>
                      <td className="py-3 pr-4">
                        {pill(
                          status === "inactive"
                            ? "border-slate-200 bg-slate-50 text-slate-600"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700",
                          status
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {c.portalLogin?.loginEnabled
                          ? pill("border-blue-200 bg-blue-50 text-blue-700", "Active")
                          : c.portalLogin?.setupToken
                          ? pill("border-amber-200 bg-amber-50 text-amber-700", "Pending")
                          : pill("border-slate-200 bg-slate-50 text-slate-500", "Not set up")}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/dashboard/customers/${c._id}`}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Open
                          </Link>
                          <Link
                            to={`/dashboard/customers/${c._id}/edit`}
                            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                          >
                            Edit
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="py-10 text-center text-slate-500">No customers found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Customers;